import classNames from "classnames";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { node, string } from "prop-types";

import I18n from "pages/_components/I18n";

function Popup({ popupButtonContent, children, popupTitleID, popupDescID, id }) {
    const [isOpen, setIsOpen] = useState(false);
    const [panelPosition, setPanelPosition] = useState(null);
    const panelRef = useRef();
    const buttonRef = useRef();

    const actionDone = () => {
        setIsOpen(false);
    };

    const updatePanelPosition = useCallback(() => {
        if (!buttonRef.current) {
            return;
        }

        const rect = buttonRef.current.getBoundingClientRect();
        setPanelPosition({
            top: rect.bottom,
            right: window.innerWidth - rect.right,
        });
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        updatePanelPosition();
        window.addEventListener("resize", updatePanelPosition);
        window.addEventListener("scroll", updatePanelPosition, true);

        return () => {
            window.removeEventListener("resize", updatePanelPosition);
            window.removeEventListener("scroll", updatePanelPosition, true);
        };
    }, [isOpen, updatePanelPosition]);

    useOnClickOutside(panelRef, buttonRef, () => setIsOpen(false));

    const childrenWithProps = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { actionDone });
        }
        return child;
    });

    const popupLayer =
        isOpen &&
        createPortal(
            <div className={classNames("popup-portal", { "popup-portal--user-menu": id === "userPopup" })}>
                <button
                    className="popup-overlay"
                    onClick={handleOverlayClick}
                    onKeyDown={handleLastTabNext}
                    type="button">
                    <I18n id="global.close" componentProps={{ className: "visually-hidden" }} />
                </button>
                <div
                    id="panelID"
                    className="popup-panel popup-panel-arrow"
                    role="dialog"
                    aria-labelledby="dialog1Title"
                    ref={panelRef}
                    style={
                        panelPosition
                            ? {
                                  top: panelPosition.top,
                                  right: panelPosition.right,
                              }
                            : undefined
                    }>
                    <h2
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                        onKeyDown={handleFirstTabPrev}
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        className="visually-hidden"
                        id="dialog1Title"
                        aria-describedby={popupDescID && "dialogDesc"}>
                        <I18n id={popupTitleID} />
                    </h2>
                    {popupDescID && (
                        <p className="visually-hidden" id="dialogDesc">
                            {popupDescID}
                        </p>
                    )}
                    {childrenWithProps}
                </div>
            </div>,
            document.body,
        );

    return (
        <div className={classNames("popup", { "popup--open": isOpen })} id={id}>
            <button
                ref={buttonRef}
                className="popup-button pl-3"
                onClick={handleClick}
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onKeyDown={handleFirstTabPrev}>
                {popupButtonContent}
            </button>
            {popupLayer}
        </div>
    );

    function handleFirstTabPrev(event) {
        if (event.shiftKey && event.key === "Tab") {
            setIsOpen(false);
        }
    }

    function handleLastTabNext(event) {
        if (event.key === "Tab" && isOpen) {
            setIsOpen(false);
        }
    }

    function handleOverlayClick() {
        setIsOpen(false);
    }

    function handleClick() {
        setIsOpen((prevState) => !prevState);
    }
}

function useOnClickOutside(panelRef, buttonRef, handler) {
    useEffect(() => {
        const listener = (event) => {
            if (panelRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) {
                return;
            }

            handler(event);
        };

        document.addEventListener("mousedown", listener);
        document.addEventListener("touchstart", listener);

        return () => {
            document.removeEventListener("mousedown", listener);
            document.removeEventListener("touchstart", listener);
        };
    }, [panelRef, buttonRef, handler]);
}

Popup.propTypes = {
    popupButtonContent: node.isRequired,
    children: node.isRequired,
    popupTitleID: string.isRequired,
    popupDescID: string,
    id: string,
};

Popup.defaultProps = {
    popupDescID: null,
    id: null,
};

export default Popup;
